// Eager because it is the first screen. Keep the rest lazy.
import LoginView from "./views/LoginView.vue";

import { installBundleHashCheck } from "./utils/bundleHashCheck";
import { installChunkReload } from "./utils/chunkReload";
import { createRouter, createWebHistory, RouteLocationNormalizedGeneric, RouteRecordRaw } from "vue-router";
import { cachedUserStatus, fetchUserStatus, isTransientSessionFailure, type User } from "./queries/session";
import { isNativePlatform } from "./capacitor/platform";
import { getServerUrl } from "./capacitor/server-url";
import { isForceUpdateActive } from "./capacitor/force-update-state";

declare module "vue-router" {
  interface RouteMeta {
    title?: string;
    favicon?: string;
    requiresUserLogin?: boolean;
    requiresAdminLogin?: boolean;
    rejectsCloudMode?: boolean;
    // Warm-up order (utils/routeWarmup.ts): lowest first.
    warm?: number;
  }
}

export const routes: RouteRecordRaw[] = [
  {
    path: "/home",
    name: "Home",
    component: () => import("./views/NewUserDashboardView.vue"),
    meta: { title: "Dashboard - Knocknoc", requiresUserLogin: true, warm: 1 },
  },
  {
    path: "/home/new",
    redirect: "/home",
  },
  {
    path: "/otp/verify",
    name: "TwoFactor",
    component: () => import("./views/TwoFactorView.vue"),
    meta: { title: "Authenticate - Knocknoc" },
  },
  {
    path: "/otp/onboard",
    name: "TwoFactorOnboard",
    component: () => import("./views/TwoFactorOnboardView.vue"),
    meta: { title: "Authenticate - Knocknoc" },
  },
  {
    path: "/otp/setup",
    name: "TwoFactorSetup",
    component: () => import("./views/TwoFactorOnboardView.vue"),
    props: { fromKnoc: true },
    meta: { title: "Set up 2FA - Knocknoc", requiresUserLogin: true },
  },
  {
    path: "/password/change",
    name: "PasswordChange",
    component: () => import("./views/ChangePasswordRequiredView.vue"),
    meta: { title: "Change password - Knocknoc", requiresUserLogin: true },
  },
  {
    path: "/session-limit",
    name: "SessionLimit",
    component: () => import("./views/SessionLimitView.vue"),
    meta: { title: "Session limit - Knocknoc", requiresUserLogin: true },
  },
  {
    path: "/connect",
    name: "ServerConnect",
    component: () => import("./views/ServerConnectView.vue"),
    meta: { title: "Connect - Knocknoc" },
  },
  {
    path: "/update-required",
    name: "ForceUpdate",
    component: () => import("./views/ForceUpdateView.vue"),
    meta: { title: "Update required - Knocknoc" },
  },
  {
    path: "/",
    name: "Login",
    component: LoginView,
    meta: { title: "Login - Knocknoc" },
  },
  {
    path: "/logged-out",
    name: "LoggedOut",
    component: () => import("./views/LoggedOutView.vue"),
    meta: { title: "Logged out - Knocknoc" },
  },
  {
    path: "/redirecting",
    name: "Redirecting",
    component: () => import("./views/RedirectingView.vue"),
    meta: { title: "Redirecting - Knocknoc" },
  },
  { path: "/:pathMatch(.*)*", redirect: "/" },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    // Same-page query/hash updates (e.g. filter and pagination URL sync) should
    // leave the scroll position alone rather than jump to the top.
    if (to.path === from.path) {
      return false;
    }
    return { top: 0 };
  },
});

function handleRouteMetadata(to: RouteLocationNormalizedGeneric) {
  const toRouteChain = to.matched;
  let title = "Knocknoc";

  // If the route doesn't have a title, get it from the closest parent
  for (let i = toRouteChain.length - 1; i >= 0; i--) {
    const route = toRouteChain[i];
    if (route.meta?.title) {
      title = route.meta.title;
      break;
    }
  }

  document.title = title;

  let favicon = "/favicon.ico";

  // If the route doesn't have a favicon, get it from the closest parent
  for (let i = toRouteChain.length - 1; i >= 0; i--) {
    const route = toRouteChain[i];
    if (route.meta?.favicon) {
      favicon = route.meta.favicon;
      break;
    }
  }

  let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = favicon;
}

router.beforeEach(async (to, _, next) => {
  handleRouteMetadata(to);

  // Force-update wall: once the app-updater has observed that the server
  // requires a newer native app version than this APK/IPA provides, every
  // route lands on the ForceUpdate screen until the app is updated.
  // ServerConnect is exempt, since a different server may not require the update.
  if (isForceUpdateActive()) {
    if (to.name !== "ForceUpdate" && to.name !== "ServerConnect") {
      next({ name: "ForceUpdate" });
      return;
    }
    next();
    return;
  }

  // On native, require a server URL before allowing any route except /connect
  if (isNativePlatform()) {
    if (to.name !== "ServerConnect") {
      const serverUrl = await getServerUrl();
      if (!serverUrl) {
        next({ name: "ServerConnect" });
        return;
      }
    }
  } else if (to.name === "ServerConnect" || to.name === "ForceUpdate") {
    // Web users should never see native-only screens
    next({ name: "Login" });
    return;
  }

  let user: User | null = null;

  try {
    if (cachedUserStatus()) {
      // Served from the cache while fresh.
      user = await fetchUserStatus();
    } else if (to.meta?.requiresUserLogin) {
      // A fresh load or postLoginNavigate may still hold a valid session cookie.
      user = await fetchUserStatus({ force: true });
    }
  } catch (error) {
    // A server that could not answer says nothing about the session.
    user = isTransientSessionFailure(error) ? (cachedUserStatus() ?? null) : null;
  }

  if (user != null) {
    if (to.name === "Login") {
      next({ name: "Home", params: to.params, query: to.query });
      return;
    }

    // Ahead of the session limit, as on the server.
    if (user.PasswordChangeRequired && to.meta?.requiresUserLogin && to.name !== "PasswordChange") {
      next({ name: "PasswordChange" });
      return;
    }
    if (!user.PasswordChangeRequired && to.name === "PasswordChange") {
      next({ name: "Home" });
      return;
    }

    // A session without a session slot (login landed on a full pool) stays on
    // the session-limit screen until it frees a slot and continues.
    if (user.HasSessionSlot === false && to.meta?.requiresUserLogin && to.name !== "SessionLimit") {
      next({ name: "SessionLimit" });
      return;
    }
    if (user.HasSessionSlot !== false && to.name === "SessionLimit") {
      next({ name: "Home" });
      return;
    }
  }

  if (to.meta) {
    if (!user && to.meta.requiresUserLogin) {
      next({ name: "Login", params: to.params, query: to.query });
      return;
    }
  }

  // For all other cases, proceed with navigation
  next();
});

installChunkReload(router);
installBundleHashCheck(router);
